import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { createImportCsvColumns } from "@/features/import-csv/-components/-preview-file/-columns/createImportCsvColumns";
import type {
  MutateTableRowsArgs,
  TableColumnDefs,
  TableRow,
  TableRows,
  UseTableRows,
} from "@/features/import-csv/types";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";

type Props = {
  uploadedFileKey?: string;
};

export const useEquipmentTableRows: UseTableRows = ({
  uploadedFileKey,
}: Props) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.equipment.importCsv.checkValidColumn",
  });

  const [tableRows, setTableRows] = useState<TableRows>({
    rows: [],
    error: {
      cellsCount: 0,
      rowsCount: 0,
      headerErrors: [],
    },
  });
  const [filterError, setFilterError] = useState(false);
  const [tableColumnDefs, setTableColumnDefs] = useState<TableColumnDefs>([]);

  const { mutate, isPending, isError } = useMutation({
    mutationKey: ["equipmentTableRows", uploadedFileKey],
    mutationFn: async ({ dryrun, filterError }: MutateTableRowsArgs) => {
      const res = await apiClient.POST("/v2/equipment-csv", {
        body: {
          encoding: "UTF-8",
          dryrun,
          uploadedFileKey: uploadedFileKey || "",
        },
      });

      const data = getDataOrThrowApiError(res);

      // APIレスポンスをTableRows形式に変換
      const allRows: TableRow[] =
        data.records?.map((record) => ({
          isValid: record.isValid,
          errorCellsCount: Object.values(record.values || {}).filter(
            (v: { errors?: string[] }) => v?.errors && v.errors.length > 0,
          ).length,
          errorCellsCountI18n: t("errors"),
          values: record.values as Record<
            string,
            { value: string; errors?: string[] }
          >,
          recordErrors: record.recordErrors,
        })) || [];

      // filterErrorがtrueの場合、エラーのある行のみフィルタリング
      const rows = filterError
        ? allRows.filter((row) => !row.isValid)
        : allRows;

      // ヘッダーデータの変換
      const headersData: Record<string, { value: string }> = {};
      if (data.headers && data.headers[0]) {
        Object.entries(data.headers[0].values).forEach(
          ([key, value]: [string, { value: string }]) => {
            headersData[key] = { value: value.value };
          },
        );
      }

      // エラー情報の構築
      const error = {
        cellsCount: allRows.reduce((acc, row) => acc + row.errorCellsCount, 0),
        rowsCount: allRows.filter((row) => !row.isValid).length,
        headerErrors: [],
      };

      return {
        rows,
        headersData,
        error,
        filterError: filterError ?? false,
      };
    },
    onSuccess: (data) => {
      setTableRows({
        rows: data.rows,
        error: data.error,
      });
      setFilterError(data.filterError);
      if (data.headersData) {
        setTableColumnDefs(createImportCsvColumns(data.headersData));
      }
    },
  });

  return {
    tableRows,
    tableColumnDefs,
    mutateTableRows: mutate,
    isPending,
    isError,
    filterError,
  };
};
